import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { FiDownload, FiHome, FiCheckCircle } from 'react-icons/fi';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ElegantPageBg from '../components/ElegantPageBg';
import { formatDateLong } from '../utils/date';
import './Ticket.css';

import logoImage from '../assets/images/vgp-logo.png';

/** Event label for PDF when API stored event_name or line items are event tickets */
function deriveEventNameFromBooking(booking) {
  if (!booking) return '';
  const direct = (booking.event_name || '').trim();
  if (direct) return direct;
  const items = booking.items || [];
  const names = [];
  const seen = new Set();
  for (const it of items) {
    const tt = it.ticket_type;
    if (tt && tt.is_event && tt.name) {
      const n = String(tt.name).trim();
      if (n && !seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    }
  }
  return names.join(', ');
}

const Ticket = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resetBooking } = useBooking();
  const booking = location.state?.booking;
  const pdfSourceRef = useRef(null);
  const bookingForPdfRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    if (booking && !bookingForPdfRef.current) {
      const frozen = {
        booking_id: booking.booking_id,
        full_name: booking.full_name,
        number_of_visitors: booking.number_of_visitors,
        visit_date: booking.visit_date,
        total_amount: booking.total_amount,
        event_name: deriveEventNameFromBooking(booking),
      };
      bookingForPdfRef.current = frozen;
      setPdfData(frozen);
    }
  }, [booking]);

  const handleDownload = async () => {
    const data = bookingForPdfRef.current;
    if (!data || !pdfSourceRef.current) return;

    try {
      const canvas = await html2canvas(pdfSourceRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 15000,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [210, 297],
        compress: true,
      });

      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const xOffset = (210 - imgWidth) / 2;
      const yOffset = 10;

      pdf.addImage(imgData, 'JPEG', xOffset, yOffset, imgWidth, imgHeight);
      pdf.setProperties({
        title: `VGP Ticket - ${data.booking_id}`,
        subject: 'VGP Wonder World Entry Ticket',
        author: 'VGP Wonder World',
      });
      pdf.save(`VGP_Ticket_${data.booking_id || 'ticket'}.pdf`);
    } catch (error) {
      console.error('Error downloading ticket PDF:', error);
      alert('Failed to download ticket. Please try again.');
    }
  };

  const handleNewBooking = () => {
    resetBooking();
    navigate('/');
  };

  if (!booking) {
    return (
      <div className="ticket-page">
        <ElegantPageBg />
        <div className="container">
          <div className="error-state">
            <h1>Ticket Not Found</h1>
            <p>We couldn't find your ticket details. Please start a new booking.</p>
            <button className="primary-button" onClick={handleNewBooking}>
              <FiHome />
              <span>Start New Booking</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ticket-page">
      <ElegantPageBg />
      <div className="container">
        {/* Success message, transaction id, booking id, and download button */}
        <div className="success-banner">
          <div className="success-header">
            <FiCheckCircle className="success-icon" />
            <h2 className="success-title">Booking Confirmed!</h2>
          </div>
          <div className="success-text">
            <p className="success-message">Your payment was successful. Your ticket is ready.</p>
            <p className="success-id-row">
              <span className="success-id-label">Payment Transaction ID:</span>
              <span className="success-id-value">{booking.internal_transaction_id || booking.transaction_id || booking.payment_id || '—'}</span>
            </p>
            <p className="success-id-row">
              <span className="success-id-label">Booking ID:</span>
              <span className="success-id-value">{booking.booking_id}</span>
            </p>
          </div>
          <div className="ticket-actions">
            <button className="action-button download-button" onClick={handleDownload}>
              <FiDownload />
              <span>Download Ticket</span>
            </button>
            <button className="action-button home-button" onClick={handleNewBooking}>
              <FiHome />
              <span>Book Another</span>
            </button>
          </div>
        </div>

        {/* Ticket template – hidden, used only for PDF download via html2canvas */}
        {pdfData && (
          <div
            ref={pdfSourceRef}
            className="ticket-pdf-source"
            style={{ position: 'fixed', left: -9999, top: 0, width: 440, zIndex: -1 }}
          >
            <div className="ticket-wrapper">
              <div className="ticket">
                <div className="ticket-header">
                  <div className="ticket-logo">
                    <img src={logoImage} alt="VGP Wonder World" className="logo-img" />
                  </div>
                  <div className="ticket-title">
                    <p className="ticket-subtitle">Entry Ticket</p>
                    {(pdfData.event_name && String(pdfData.event_name).trim()) ? (
                      <p className="ticket-event-name">{String(pdfData.event_name).trim()}</p>
                    ) : null}
                  </div>
                </div>
                <div className="ticket-content">
                  <div className="ticket-section">
                    <h2 className="section-title">
                      <span className="title-icon">📋</span>
                      Booking Details
                    </h2>
                    <div className="ticket-details-grid">
                      <div className="ticket-detail-item">
                        <span className="detail-label">Booking Name</span>
                        <span className="detail-value">{pdfData.full_name || 'N/A'}</span>
                      </div>
                      <div className="ticket-detail-item">
                        <span className="detail-label">Number of Persons</span>
                        <span className="detail-value">{pdfData.number_of_visitors ?? 1}</span>
                      </div>
                      <div className="ticket-detail-item">
                        <span className="detail-label">Visiting Date</span>
                        <span className="detail-value">{formatDateLong(pdfData.visit_date)}</span>
                      </div>
                      <div className="ticket-detail-item highlight">
                        <span className="detail-label">Total Amount</span>
                        <span className="detail-value amount">₹ {parseFloat(pdfData.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ticket-section qr-section">
                    <div className="qr-container">
                      <div className="qr-code-wrapper">
                        <QRCode
                          value={pdfData.booking_id || ''}
                          size={120}
                          level="H"
                          style={{ height: 'auto', maxWidth: '100%' }}
                          fgColor="#000000"
                          bgColor="#ffffff"
                        />
                      </div>
                      <div className="qr-label">
                        <p className="qr-booking-id">Booking ID: {pdfData.booking_id}</p>
                        <p className="qr-instruction">Scan this QR code at the entrance</p>
                      </div>
                    </div>
                  </div>
                  <div className="ticket-footer">
                    <p className="footer-note">
                      Please present this ticket at the entrance. Keep this ticket safe for your visit.
                    </p>
                    <p className="footer-contact">
                      For queries, contact: support@vgpuniversalkingdom.com
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ticket;
